import React from "react";
import Accordion from "../../../common/accordion";
import { useAppSelector } from "../../../../store/hooks/StoreHooks";
import useCollapseFacade from "../../../../facade/collapse.Facade";
import { LABELS } from "../../../../constants/labels/help.labels";

const BasicCalculations: React.FC = () => {
    const { basicWIFCollapse, basicIPRCollapse, basicTDHCollapse } =
        useAppSelector((state) => state?.collapse);

    const {
        updateBasicWIFCollapse,
        updateBasicIPRCollapse,
        updateBasicTDHCollapse,
    } = useCollapseFacade();

    const handleCollapseChange = (key: boolean, name: string) => {
        if (name === "WIF") updateBasicWIFCollapse(key);
        else if (name === "IPR") updateBasicIPRCollapse(key);
        else if (name === "TDH") updateBasicTDHCollapse(key);
    };

    return (
        <div
            className="flex flex-col justify-center item-center w-full h-full gap-[16px] text-white"
            id="Basic-Calculations"
        >
            <Accordion
                title={LABELS?.wellInflowFormulas}
                id={"WIF"}
                isOpen={!basicWIFCollapse}
                onToggle={() => handleCollapseChange(!basicWIFCollapse, "WIF")}
                content={
                    <div className="flex flex-col justify-center item-center p-[16px] bg-darkcolor-800 w-full h-full gap-[16px] rounded-br-lg rounded-bl-lg">
                        <div className="pad-all border-[1px] border-darkcolor-400 bg-darkcolor-700 rounded-lg">
                            <table className="align-center table w-full dtr-inline">
                                <colgroup>
                                    <col style={{ width: "10%", borderRight: "1px solid #4a5463" }} />
                                    <col
                                        style={{ width: "23%", borderRight: "1px solid #4a5463" }}
                                    />
                                    <col
                                        style={{ width: "23%", borderRight: "1px solid #4a5463" }}
                                    />
                                    <col
                                        style={{ width: "23%", borderRight: "1px solid #4a5463" }}
                                    />
                                    <col style={{ width: "21%" }} />
                                </colgroup>
                                <tr className="align-center border-b-[1px] border-darkcolor-400 text-[14px] font-normal">
                                    <th className="align-center px-[24px] py-[16px]"></th>
                                    <th className="align-center px-[24px] py-[16px]">Wiggins</th>
                                    <th className="align-center px-[24px] py-[16px]">Vogel</th>
                                    <th className="align-center px-[24px] py-[16px]">Composite</th>
                                    <th className="align-center px-[24px] py-[16px]">Constant PI</th>
                                </tr>
                                <tr className="align-center border-b-[1px] border-darkcolor-400 text-[14px] font-normal">
                                    <th className="align-center px-[24px] py-[16px] font-normal">Max flow rate</th>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary pb-4">
                                            Qomax_Wiggins + Qwmax_Wiggins;
                                        </p>
                                        <div className="pb-4">
                                            <p className="secondary">Qomax_Wiggins :</p>
                                            <p className="secondary-text">
                                                (oilflowRate / (1 - 0.52 * (FlowBHPressure /
                                                StaticBHPressure) - 0.48 * ((FlowBHPressure /
                                                StaticBHPressure)^ 2)))
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Qwmax_Wiggins :</p>
                                            <p className="secondary-text">
                                                (waterFlowRate / (1 - 0.72 * (FlowBHPressure /
                                                StaticBHPressure) - 0.28 * ((FlowBHPressure /
                                                StaticBHPressure)^ 2)))
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">oilflowRate :</p>
                                            <p className="secondary-text">
                                                TestFlowRate * Fo
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">waterFlowRate :</p>
                                            <p className="secondary-text">
                                                TestFlowRate * Fw
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Fo :</p>
                                            <p className="secondary-text">1 - Fw</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Fw :</p>
                                            <p className="secondary-text">
                                                (watercut / 100)
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">watercut :</p>
                                            <p className="secondary-text">Fluid tab</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">
                                                TestFlowRate, FlowBHPressure, StaticBHPressure :
                                            </p>
                                            <p className="secondary-text">Well tab</p>
                                        </div>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (TestFlowRate / (1 - 0.2 * (FlowBHPressure /
                                            StaticBHPressure) - 0.8 * (FlowBHPressure /
                                            StaticBHPressure)^2))
                                        </p>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <div className="pb-4 secondary">
                                            (Qomax_Composite + Fw * (StaticBHPressure -
                                            (Qomax_Composite / CompositePI)) / Slope)
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Qomax_Composite :</p>
                                            <p className="secondary-text">
                                                (Qb + CompositePI * (BubblePtPressure / 1.8))
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Qb :</p>
                                            <p className="secondary-text">
                                                (CompositePI * (StaticBHPressure - BubblePtPressure))
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Fw :</p>
                                            <p className="secondary-text">watercut / 100</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Fo :</p>
                                            <p className="secondary-text">1 - Fw</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">CompositePI :</p>
                                            <p className="secondary-text">
                                                FlowRate / (StaticBHPressure - FlowBHPressure)
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Slope :</p>
                                            <p className="secondary-text">CD / CG</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">CD :</p>
                                            <p className="secondary-text">
                                                ((Fw * ((0.001 * Qomax_Composite) / CompositePI)) + (Fo
                                                * 0.125 * BubblePtPressure * (-1 + ((81 - 80 * ((0.999 *
                                                Qomax_Composite - Qb) / (Qomax_Composite - Qb))),
                                                0.5)^2)))
                                            </p>
                                        </div>
                                        <div className="">
                                            <p className="secondary">CG :</p>
                                            <p className="secondary-text">
                                                0.001 * Qomax_Composite
                                            </p>
                                        </div>
                                    </td>
                                    <td className="col-2 px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (TestFlowRate / (StaticBHPressure - FlowBHPressure)) *
                                            StaticBHPressure
                                        </p>
                                    </td>
                                </tr>
                                <tr className="align-center border-b-[1px] border-darkcolor-400 text-[14px] font-normal">
                                    <th className="align-center px-[24px] py-[16px] font-normal">Additional BPD</th>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary pb-4">
                                            MaxFlowRate - DesiredProductionRate
                                        </p>
                                        <div className="">
                                            <p className="secondary">DesiredProductionRate :</p>
                                            <p className="secondary-text">Well tab</p>
                                        </div>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (TestFlowRate / (1 - 0.2 * (FlowBHPressure /
                                            StaticBHPressure) - 0.8 * (FlowBHPressure /
                                            StaticBHPressure)^2)) - ProductionRate
                                        </p>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <div className="secondary pb-4">
                                            (Qtmax_Composite - ProductionRate)
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Qomax_Composite :</p>
                                            <p className="secondary-text">
                                                (Qb + CompositePI * (BubblePtPressure / 1.8))
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Qb :</p>
                                            <p className="secondary-text">
                                                (CompositePI * (StaticBHPressure - BubblePtPressure))
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Fw :</p>
                                            <p className="secondary-text">watercut / 100</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Fo :</p>
                                            <p className="secondary-text">1 - Fw</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">CompositePI :</p>
                                            <p className="secondary-text">
                                                FlowRate / (StaticBHPressure - FlowBHPressure)
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Slope :</p>
                                            <p className="secondary-text">CD / CG</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">CD :</p>
                                            <p className="secondary-text">
                                                ((Fw * ((0.001 * Qomax_Composite) / CompositePI)) + (Fo
                                                * 0.125 * BubblePtPressure * (-1 + ((81 - 80 * ((0.999 *
                                                Qomax_Composite - Qb) / (Qomax_Composite - Qb))),
                                                0.5)^2)))
                                            </p>
                                        </div>
                                        <div className="">
                                            <p className="secondary">CG :</p>
                                            <p className="secondary-text">
                                                0.001 * Qomax_Composite
                                            </p>
                                        </div>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">
                                            ((TestFlowRate / (StaticBHPressure - FlowBHPressure)) *
                                            StaticBHPressure) - ProductionRate
                                        </p>
                                    </td>
                                </tr>
                                <tr className="align-center border-b-[1px] border-darkcolor-400 text-[14px] font-normal">
                                    <th className="px-[24px] py-[16px] font-normal font-normal">PI</th>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">Not applicable</p>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">
                                            FlowingBottomHolePressureVogelValue * 6.89476
                                        </p>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (FlowRate / ((1 - (watercut / 100)) * (StaticBHPressure -
                                            StaticBHPressure + StaticBHPressure * (1 - 0.2 *
                                            (FlowBHPressure / StaticBHPressure) - 0.8 *
                                            ((FlowBHPressure / StaticBHPressure)^2)) / 1.8) +
                                            (watercut / 100) * (StaticBHPressure - FlowBHPressure)))
                                        </p>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">
                                            FlowRate / (StaticBHPressure - FlowBHPressure)
                                        </p>
                                    </td>
                                </tr>
                                <tr className="align-center text-[14px] font-normal">
                                    <th className="px-[24px] py-[16px] font-normal font-normal">
                                        Flowing bottom hole pressure @ desired production rate
                                    </th>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <div className="secondary pb-4">
                                            If (ProductionFraction) is equal to 0 or 1
                                            <br /> Then value will be 0
                                        </div>
                                        <div className="secondary pb-4">
                                            If (ProductionFraction) is not equal to 0 or 1 then - SBHP
                                            * FBHPFraction
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">FBHPFraction :</p>
                                            <p className="secondary-text">
                                                (V5 * FutureProductionFraction^6 + V4 *
                                                FutureProductionFraction^5 + V3 *
                                                FutureProductionFraction^4 + V2 *
                                                FutureProductionFraction^3 + V1 *
                                                FutureProductionFraction^2 + V0 *
                                                FutureProductionFraction + 1)
                                            </p>
                                        </div>
                                        <div className="">
                                            <p className="secondary">ProductionFraction :</p>
                                            <p className="secondary-text">
                                                DesiredProductionRate / MaxFlowRate
                                            </p>
                                        </div>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <div className="secondary pb-4">SBHP * FBHPFraction</div>
                                        <div className="">
                                            <p className="secondary">FBHPFraction :</p>
                                            <p className="secondary-text">
                                                V0 + V1 * ProductionFraction + V2 * (ProductionFraction
                                                ^ 2) + V3 * (ProductionFraction ^ 2 ) + V4 *
                                                (ProductionFraction ^ 2) + V5 * (ProductionFraction ^ 2)
                                            </p>
                                        </div>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (StaticBHPressure - (ProductionRate / (FlowRate / ((1 -
                                            (watercut / 100)) * (StaticBHPressure - StaticBHPressure +
                                            StaticBHPressure * (1 - 0.2 * (FlowBHPressure /
                                            StaticBHPressure) - 0.8 * ((FlowBHPressure /
                                            StaticBHPressure)^2)) / 1.8) + (watercut / 100) *
                                            (StaticBHPressure - FlowBHPressure)))))
                                        </p>
                                    </td>
                                    <td className="align-center px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (StaticBHPressure - ProductionRate / (FlowRate /
                                            (StaticBHPressure - FlowBHPressure)))
                                        </p>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                }
            ></Accordion>
            <Accordion
                title={LABELS?.wellIPRFormulas}
                id={"IPR"}
                isOpen={!basicIPRCollapse}
                onToggle={() => handleCollapseChange(!basicIPRCollapse, "IPR")}
                content={
                    <div className="flex justify-center items-center p-[16px] bg-darkcolor-800 gap-4 w-full text-white rounded-br-lg rounded-bl-lg">
                        <div className="pad-all w-full bg-darkcolor-700 border-[1px] border-darkcolor-400 rounded-lg">
                            <table className="table-auto w-full border-collapse">
                                <colgroup>
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "40%", borderRight: "1px solid #4a5463" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "40%" }} />
                                </colgroup>
                                <tr className="align-center text-[14px] font-normal border-b-[1px] border-darkcolor-400">
                                    <th className="px-[24px] py-[16px] font-normal font-normal">Future shutIn pressure</th>
                                    <td className="px-[24px] py-[16px]">
                                        <div className="pb-4 secondary">
                                            Selected depletion pressure from ESP catalog
                                            (Dr_ProjectValues table in database)
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">
                                                If no value in ESP catalog :
                                            </p>
                                            <p className="secondary-text">
                                                If inflow type is PI or Pressure
                                                <br />
                                                Depletion pressure = Static bottom hole pressure (Well tab)
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">If inflow type is Head </p>
                                            <p className="secondary-text">
                                                depletionPressure = (staticFluidLevel * 0.433) *
                                                finalCompositeSpGr;
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">StaticFluidLevel :</p>
                                            <p className="secondary-text">Well tab</p>
                                        </div>
                                        <div className="">
                                            <p className="secondary">finalCompositeSpGr :</p>
                                            <p className="secondary-text">Fluid tab</p>
                                        </div>
                                    </td>
                                    <th className="px-[24px] py-[16px] font-normal font-normal">Depletion %</th>
                                    <td className="px-[24px] py-[16px]">
                                        <div className="pb-4 secondary">
                                            (Pressure - DepletionPressure) / Pressure * 100;
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">DepletionPressure :</p>
                                            <p className="secondary-text">
                                                Basic calculation output tab
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">
                                                If inflow type is Pressure :
                                            </p>
                                            <p className="secondary-text">
                                                Pressure = Static bottom hole pressure (Well tab)
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">If inflow type is PI :</p>
                                            <p className="secondary-text">
                                                If StaticBHP is greater than 0 AND SticFluidLevel is
                                                less than or equal to 0
                                                <br /> Pressure = Static bottom hole pressure (Well tab)
                                                <br />
                                                If StaticBHP is equal to 0 AND StaticFluidLevel is
                                                greater than 0
                                                <br />
                                                Pressure = CasingPressure + ((Datum - StaticFluidLevel)
                                                * CompositeSpGr / 2.31)
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">If inflow type is Head :</p>
                                            <p className="secondary-text">
                                                Pressure = CasingPressure + ((Datum - StaticFluidLevel)
                                                * CompositeSpGr / 2.31)
                                            </p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">CasingPressure :</p>
                                            <p className="secondary-text">Well tab</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">Datum :</p>
                                            <p className="secondary-text">Well tab</p>
                                        </div>
                                        <div className="pb-4">
                                            <p className="secondary">StaticFluidLevel :</p>
                                            <p className="secondary-text">Well tab</p>
                                        </div>
                                        <div className="">
                                            <p className="secondary">CompositeSpGr :</p>
                                            <p className="secondary-text">Fluid tab</p>
                                        </div>
                                    </td>
                                </tr>
                                <tr className="align-center text-[14px] font-normal">
                                    <th className="px-[24px] py-[16px] font-normal font-normal">Future production rate</th>
                                    <td className="px-[24px] py-[16px]">
                                        <div className="pb-4 secondary">
                                            Selected FutureProductionRate from ESP catalog
                                            (Dr_ProjectValues table in database)
                                        </div>
                                        <div className="">
                                            <p className="secondary">
                                                If no value in ESP catalog :
                                            </p>
                                            <p className="secondary-text">
                                                Then default value is 1000
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                }
            />
            <Accordion
                title={LABELS?.tdhFormulas}
                id={"TDH"}
                isOpen={!basicTDHCollapse}
                onToggle={() => handleCollapseChange(!basicTDHCollapse, "TDH")}
                content={
                    <div className="flex justify-center items-center bg-darkcolor-800 p-[16px] w-full rounded-br-lg rounded-bl-lg">
                        <div className="pad-all w-full bg-darkcolor-700 border-[1px] rounded-lg border-darkcolor-400">
                            <table className="table-auto w-full border-collapse">
                                <colgroup>
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "40%", borderRight: "1px solid #4a5463" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "40%" }} />
                                </colgroup>
                                <tr className="align-center border-b-[1px] border-darkcolor-400 text-[14px] font-normal">
                                    <th className="px-[24px] py-[16px] font-normal">Tubing head</th>
                                    <td className="px-[24px] py-[16px]">
                                        <p className="secondary">
                                            Pressure * 2.31 / Liquid phase SpGr
                                        </p>
                                    </td>
                                    <th className="px-[24px] py-[16px] font-normal">Required main pump TDH</th>
                                    <td className="px-[24px] py-[16px]">
                                        <p className="secondary pb-4">
                                            ProductionLevel + FrictionLoss + TubingHead
                                        </p>
                                        <p className="secondary">ProductionLevel :</p>
                                        <p className="secondary-text pb-4">TVD - (((finalPip - CasingPressure) *
                                            2.31) / LiquidPhaseSpGr)</p>
                                        <p className="secondary">finalPip :</p>
                                        <p className="secondary-text pb-4">From gas calculation output (Right most value)</p>
                                        <p className="secondary">TVD:</p>
                                        <p className="secondary-text pb-4">Pump setting @ vertical depth (Well tab)</p>
                                        <p className="secondary">CasingPressure:</p>
                                        <p className="secondary-text">Well tab</p>
                                    </td>
                                </tr>
                                <tr className="align-center border-b-[1px] border-darkcolor-400 text-[14px] font-normal">
                                    <th className="px-[24px] py-[16px] font-normal">Friction loss</th>
                                    <td className="px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (2.083 *(100 / (tubingAge , 1.85)^2 ) * ((productionRate /
                                            34.3),1.85)^2 * (tubingID , 4.8655)^2 / (1000 *
                                            pumpMeasuredDepth) )
                                        </p>
                                    </td>
                                    <th className="px-[24px] py-[16px] font-normal">Required system TDH</th>
                                    <td className="px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (pumpVerticalDepth - flop + frictionLoss + tubingHead)
                                        </p>
                                    </td>
                                </tr>
                                <tr className="align-center border-b-[1px] border-darkcolor-400 text-[14px]">
                                    <th className="px-[24px] py-[16px] font-normal">Head at pump intake</th>
                                    <td className="px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (StaticBottomHolePressure - ProductionRate / PI) -
                                            ((Perforation - pumpMeasuredDepth) * CombinedSpGr / 2.31)
                                            + casingpressure
                                        </p>
                                    </td>
                                    <th className="px-[24px] py-[16px] font-normal">Pump intake pressure</th>
                                    <td className="px-[24px] py-[16px]">
                                        <p className="secondary">GasCalculationPressureValue</p>
                                    </td>
                                </tr>
                                <tr className="align-center text-[14px]">
                                    <th className="px-[24px] py-[16px] font-normal">Net vertical lift</th>
                                    <td className="px-[24px] py-[16px]">
                                        <p className="secondary">(pumpVerticalDepth - FLOP)</p>
                                    </td>
                                    <th className="px-[24px] py-[16px] font-normal">New TDH</th>
                                    <td className="px-[24px] py-[16px]">
                                        <p className="secondary">
                                            (pumpVerticalDepth - (tubingPressure) * 2.31 /
                                            CombinedSpGr) + FrictionLoss + TubingHead
                                        </p>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                }
            />
        </div>
    );
};

export default BasicCalculations;
